'use client';

import React, { memo, useState, Suspense, lazy } from 'react';
import { LoadingSpinner } from '@/components/ui/design-system';


// Lazy load the MapSection for better performance
const MapSection = lazy(() => import('./MapSection'));

// Import other sections
import ContactHero from './ContactHero';
import ProductsSection from './ProductsSection';
import ContactFormSection from './ContactFormSection';

/**
 * ContactContent Component
 * 
 * Main content component that orchestrates all contact page sections with proper
 * semantic HTML structure, accessibility features, and responsive design.
 * Manages product selection state and lazy loads heavy components for performance.
 * 
 * @returns {JSX.Element} Optimized contact content with a11y and SEO compliance
 */
const ContactContent = memo(() => {
  const [selectedProduct, setSelectedProduct] = useState<'fridgeBottle' | 'makhanaJar' | null>(null);

  // Handle product selection
  const handleProductSelect = (product: 'fridgeBottle' | 'makhanaJar') => {
    setSelectedProduct(product);
    
    const element = document.getElementById('contact-form');
    
    // Smooth scroll to the form
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Focus the first input field (first name) after scrolling
      setTimeout(() => {
        const firstNameInput = document.querySelector('#contact-form input:first-of-type');
        if (firstNameInput instanceof HTMLElement) {
          firstNameInput.focus();
        }
      }, 500); // Allow time for smooth scroll
    }
  };

  return (
    <main id="main-content" className="min-h-screen bg-white dark:bg-gray-900" role="main">
      <ContactHero />
      <ProductsSection onProductSelect={handleProductSelect} />
      <ContactFormSection selectedProduct={selectedProduct} />
      <Suspense fallback={
        <div className="py-16 flex justify-center" role="status" aria-label="Loading location information">
          <LoadingSpinner size="lg" />
          <span className="sr-only">Loading location and contact information</span>
        </div>
      }>
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <MapSection />
          </div>
        </section>
      </Suspense>
    </main>
  );
});

ContactContent.displayName = 'ContactContent';

export default ContactContent;
